import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  TableRow,
  TableRowColumn,
} from 'material-ui/Table';
import DataTables from 'material-ui-datatables';
import IconButton from 'material-ui/IconButton';
import FlatButton from 'material-ui/FlatButton';
import ActionEdit from 'material-ui/svg-icons/editor/mode-edit';
import ActionRemove from 'material-ui/svg-icons/action/delete';
import FloatingActionButton from 'material-ui/FloatingActionButton';
import ContentAdd from 'material-ui/svg-icons/content/add';
import FontIcon from 'material-ui/FontIcon';
import Chip from 'material-ui/Chip';
import _ from 'lodash';

import { showDialog, hideDialog, isDialogOpen } from '../../actions/dialogs';
import { fetchEmployees, setEmployee } from '../../actions/employees';
import Dashboard from '../Dashboard';
import Employee from '../Employee';
import { removeEmployeeRoutine, fetchStaticRoutine } from '../../routines';
import { sortStrings, contains, getOrderDirection, toArray } from '../../utils/helper';
import { hasPermission } from '../../utils/authenticated';
import Loading from '../Common/Loading';

import styles from './styles';

const renderPermissions = (employee) => {
  return _.keys(employee.permissions || {}).map((permission) => <Chip
    labelStyle={styles.chipLabelStyle}
    style={styles.chip}
    key={`${employee.id}-${permission}`}
  >{permission}</Chip>);
};

const renderTags = (employee) => {
  return (employee.tags || []).map((tag, idx) => <Chip
    labelStyle={styles.chipLabelStyle}
    style={styles.chip}
    key={`${employee.id}-${tag}-${idx}`}
  >{tag}</Chip>);
};

const getTableColumns = (editable, onEdit, onRemove) => {
  return _.flatten([
    {
      key: 'name',
      label: 'Name',
      render: (name) => name,
      sortable: true,
      style: styles.nameCol,
    }, {
      key: 'mobile',
      label: 'Mobile',
      render: (mobile) => mobile,
      style: styles.mobileCol,
    }, {
      key: 'email',
      label: 'Email',
      render: (email) => email,
      sortable: true,
      style: styles.emailCol,
    }, {
      key: 'permissions',
      label: 'Permissions',
      render: (permissions, employee) => renderPermissions(employee),
      style: styles.permissionCol,
    }, {
      key: 'tags',
      label: 'Tags',
      render: (tags, employee) => renderTags(employee),
      style: styles.tagsCol,
    }, {
      key: 'action',
      label: '',
      render: (a, employee) => {
        return (<span>{ editable ? <IconButton
          tooltip="Edit"
          tooltipPosition="bottom-center"
          onClick={(evt) => {
            evt.stopPropagation();
            onEdit(employee);
          }}>
          <ActionEdit />
        </IconButton> : null }
          { editable ? <IconButton
            tooltip="Remove"
            tooltipPosition="bottom-center"
            onClick={(evt) => {
              evt.stopPropagation();
              onRemove(employee);
            }}>
            <ActionRemove />
          </IconButton> : null }
          </span>);
      },
      alignRight: true,
      style: styles.actionCol,
    },
  ]);
};

const getSorter = (sorter = (a, b) => 0, order = 'asc') => {
  return (a, b) => {
    let result = sorter(a, b);

    if (result === 0) {
      result = sortStrings(a.name, b.name);
    }

    return result * getOrderDirection(order);
  };
};

const getFilter = (filter) => {
  return (employee) => {
    if (filter) {
      if (contains(employee.name, filter)) {
        return true;
      }
      if (contains(employee.email, filter)) {
        return true;
      }

      return false;
    }

    return true;
  };
}

const getData = (allData, sortBy, filter, page, limit) => {
  const data = allData.filter(getFilter(filter ? filter.trim().toLowerCase() : '')).sort(sortBy);
  const result = {
    page: Math.min(page, Math.floor(data.length / limit) + 1),
    data,
    filter,
  };

  return result;
}

class Employees extends Component {

  static propTypes = {
    fetchPermissions: PropTypes.func.isRequired,
    fetchEmployees: PropTypes.func.isRequired,
    setEmployee: PropTypes.func.isRequired,
    removeEmployee: PropTypes.func.isRequired,
    employees: PropTypes.array.isRequired,
    showDialog: PropTypes.func.isRequired,
    hideDialog: PropTypes.func.isRequired,
    dialogs: PropTypes.object.isRequired,
  };

  state = {
    selectedRows: [],
    page: 1,
    limit: 10,
    sortBy: getSorter(),
    order: 'asc',
    data: [],
  };

  componentWillMount() {
    this.props.fetchEmployees();
    this.props.fetchPermissions();

    const { sortBy, filter, page, limit } = this.state;
    this.setState(_.assign({
      editable: hasPermission('Administrator'),
    }, getData(this.props.employees, sortBy, filter, page, limit)));
  }

  componentWillReceiveProps(nextProps) {
    const { sortBy, filter, page, limit } = this.state;
    this.setState(getData(nextProps.employees, sortBy, filter, page, limit));
  }

  handleFilterValueChange = (filter) => {
    const { employees } = this.props;
    const { page, limit, sortBy } = this.state;
    this.setState(getData(employees, sortBy, filter, page, limit));
  };

  handleSortOrderChange = (key, order) => {
    let sortBy = getSorter();
    // your sort logic
    console.log('handleSortOrderChange', key, order);
    switch (key) {
      case 'name':
        sortBy = getSorter((a, b) => sortStrings(a.name, b.name), order);
        break;
      case 'email':
        sortBy = getSorter((a, b) => sortStrings(a.email, b.email), order);
        break;
      default:
        break;
    }

    const { filter, page, limit } = this.state;
    this.setState(getData(this.props.employees, sortBy, filter, page, limit));
  };

  handlePreviousPageClick = () => {
    this.setState({
      page: Math.max(1, this.state.page - 1),
    });
  };

  handleNextPageClick = () => {
    const { data, page, limit } = this.state;

    this.setState({
      page: Math.min(Math.ceil(data.length / limit), page + 1),
    });
  };

  handleRowSizeChange = (a, limit) => {
    this.setState({
      limit,
      page: 1,
    });
  };

  handleRowSelection = (evt) => {
    let selectedRows = [];
    switch (evt) {
      case 'all':
        selectedRows = _.range(0, this.state.limit);
        break;
      case 'none':
        selectedRows = [];
        break;
      default:
        selectedRows = evt;
        break;
    }

    this.setState({
      selectedRows,
    });
  };

  handleEdit = (employee) => {
    this.props.setEmployee(employee);
    this.props.showDialog('employee');
  };

  handleRemove = (employee) => {
    this.handleRemoveEmployees([employee]);
  };

  handleMultiRemove = () => {
    if (this.state.selectedRows.length < 1) {
      return null;
    }

    const employees = this.state.paginatedEmployees.filter((employee, index) => {
      return ((this.state.selectedRows.map((val) => val)).includes(index));
    });

    this.handleRemoveEmployees(employees);

    return true;
  };

  handleRemoveEmployees = (employees) => {
    const context = {
      type: 'warning',
      title: 'Remove Employees?',
      message: `Are you sure you wish to remove the ${employees.length > 1 ? employees.length : ''}
      selected ${employees.length > 1 ? 'employees' : 'employee'}?`,
      buttons: [
        <FlatButton
          label="Cancel"
          onClick={() => this.props.hideDialog('confirm')}
        />,
        <FlatButton
          label="Remove"
          onClick={() => {
            this.props.hideDialog('confirm');
            employees.map(this.props.removeEmployee);
          }}
        />,
      ],
    };

    this.props.showDialog('confirm', context);
  };

  renderNoData() {
    return (<TableRow key="nodata">
      <TableRowColumn colSpan={6} style={styles.noDataRow}>
        <FontIcon className="material-icons" style={styles.noData}>person</FontIcon>
        <div>No employees are available</div>
      </TableRowColumn>
    </TableRow>);
  }

  render() {
    const { loading } = this.props;
    const { editable, page, limit, data } = this.state;

    return (
      <Dashboard title="Employees">
        <div style={styles.wrapper}>
          { !loading ? <DataTables
            height={'auto'}
            selectable
            multiSelectable
            showRowHover
            columns={getTableColumns(editable, this.handleEdit, this.handleRemove)}
            data={data.slice((page - 1) * limit, page * limit)}
            showCheckboxes
            enableSelectAll
            selectedRows={this.state.selectedRows}
            onFilterValueChange={this.handleFilterValueChange}
            onSortOrderChange={this.handleSortOrderChange}
            onPreviousPageClick={this.handlePreviousPageClick}
            onNextPageClick={this.handleNextPageClick}
            onRowSizeChange={this.handleRowSizeChange}
            onRowSelection={this.handleRowSelection}
            page={page}
            rowSize={limit}
            rowSizeList={[10, 15, 25, 50, 100]}
            count={data.length}
            filterHintText="Filter"
            headerToolbarMode="filter"
            showHeaderToolbar
          /> : <Loading/> }
        </div>

        { editable ? <FloatingActionButton
          style={styles.addItem}
          secondary
          onClick={() => {
            this.props.setEmployee({});
            this.props.showDialog('employee');
          }}
        >
          <ContentAdd />
        </FloatingActionButton> : null}

        { isDialogOpen(this.props.dialogs, 'employee') ? <Employee /> : null}
      </Dashboard>
    );
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchPermissions: () => fetchStaticRoutine.trigger({ type: 'permissions' }),
    fetchEmployees,
    setEmployee,
    removeEmployee: removeEmployeeRoutine.trigger,
    showDialog,
    hideDialog,
  }, dispatch);
}

function mapStateToProps(state) {
  const employees = state.get('employees').get('employees').toJS()
    .sort(getSorter());
  return {
    permissions: toArray(state.get('static').get('permissions').toJS()),
    employees,
    loading: state.get('employees').get('loading'),
    dialogs: state.get('dialogs').get('dialogs').toJS(),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Employees);



// WEBPACK FOOTER //
// ./src/components/Employees/index.js